import React from 'react';
import { BattleUnitState, StatusEffect } from '../types/battle';

interface BattleUnitInfoPanelProps {
  displayUnit: BattleUnitState;
  isCurrentTurn: boolean;
}

function getEffectIcon(effectType: string): string {
  switch (effectType) {
    case 'Poison': return '🧪';
    case 'Burn': return '🔥';
    case 'Invisibility': return '👻';
    case 'Root': return '🌿';
    case 'DamageBuff': return '⚔️';
    case 'DamageReduction': return '🛡️';
    case 'StatBuff': return '✨';
    case 'Stun': return '💫';
    default: return '📋';
  }
}

const StatBar: React.FC<{ label: string; current: number; max: number; color: string }> = ({ label, current, max, color }) => (
  <div className="info-cell" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <span className="label">{label}</span>
      <span className="value" style={{ fontSize: '12px', color: '#d2c09a' }}>{current}/{max}</span>
    </div>
    <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
      <div
        style={{
          height: '100%',
          width: `${Math.min(100, max > 0 ? (current / max) * 100 : 0)}%`,
          backgroundColor: color,
          borderRadius: '2px',
          transition: 'width 0.4s ease'
        }}
      />
    </div>
  </div>
);

const BattleUnitInfoPanel: React.FC<BattleUnitInfoPanelProps> = ({ displayUnit, isCurrentTurn }) => {
  const { stats, equipment, statusEffects } = displayUnit;

  return (
    <div className="unit-details">
      <h3>Unit Details {isCurrentTurn ? '(Current Turn)' : ''}</h3>
      <div className="unit-info">
        <h4>{displayUnit.name}</h4>
        <p><strong>Type:</strong> {displayUnit.type}</p>
        {displayUnit.race && <p><strong>Race:</strong> {displayUnit.race}</p>}
        {displayUnit.characterClass && (
          <p><strong>Class:</strong> {displayUnit.evolvedClass ? `${displayUnit.evolvedClass} (${displayUnit.characterClass})` : displayUnit.characterClass}</p>
        )}
        {displayUnit.enemyType && <p><strong>Enemy Type:</strong> {displayUnit.enemyType}</p>}
        <p><strong>Position:</strong> ({displayUnit.position.x}, {displayUnit.position.y})</p>

        <div className="unit-stats">
          <StatBar label="HP" current={stats?.currentHP || 0} max={stats?.maxHP || 0} color="#4CAF50" />
          <StatBar label="AP" current={stats?.currentAP || 0} max={stats?.maxAP || 0} color="#FF9800" />
          <StatBar label="Move" current={stats?.currentMovement || 0} max={stats?.maxMovement || 0} color="#9C27B0" />
        </div>

        {equipment?.weapon && (
          <div style={{ marginTop: '10px', padding: '8px 12px', background: 'rgba(196,154,42,0.08)', border: '1px solid rgba(196,154,42,0.25)', borderLeft: '3px solid #c49a2a', borderRadius: '3px' }}>
            <p style={{ margin: '0 0 6px', fontFamily: "'Exo 2', sans-serif", fontWeight: 700, fontSize: '13px', color: '#e6b84a' }}>
              ⚔️ {equipment.weapon.itemName || 'Weapon'}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', background: '#243344', border: '1px solid #243344', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ background: '#111e2e', padding: '5px 10px' }}>
                <span style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#485e70' }}>DMG</span>
                <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '13px', fontWeight: 600, color: '#d2c09a' }}>{equipment.weapon.minDamage}-{equipment.weapon.maxDamage}</div>
              </div>
              <div style={{ background: '#111e2e', padding: '5px 10px' }}>
                <span style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#485e70' }}>RANGE</span>
                <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '13px', fontWeight: 600, color: '#d2c09a' }}>{equipment.weapon.minRange}-{equipment.weapon.maxRange}</div>
              </div>
              <div style={{ background: '#111e2e', padding: '5px 10px' }}>
                <span style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#485e70' }}>AP COST</span>
                <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '13px', fontWeight: 600, color: '#d2c09a' }}>{equipment.weapon.weaponAPCost || 1}</div>
              </div>
              <div style={{ background: '#111e2e', padding: '5px 10px' }}>
                <span style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#485e70' }}>TYPE</span>
                <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '13px', fontWeight: 600, color: '#d2c09a' }}>{equipment.weapon.damageType || 'Physical'}</div>
              </div>
              {equipment.weapon.aoeShape && equipment.weapon.aoeShape !== 'Single' && (
                <div style={{ background: '#111e2e', padding: '5px 10px', gridColumn: '1 / -1' }}>
                  <span style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#485e70' }}>AOE</span>
                  <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '13px', fontWeight: 600, color: '#d94444' }}>{equipment.weapon.aoeShape}</div>
                </div>
              )}
              {equipment.weapon.usesAmmo && (
                <div style={{ background: '#111e2e', padding: '5px 10px', gridColumn: '1 / -1' }}>
                  <span style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#485e70' }}>AMMO</span>
                  <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '13px', fontWeight: 600, color: equipment.weapon.currentAmmo === 0 ? '#d94444' : '#d2c09a' }}>{equipment.weapon.currentAmmo}/{equipment.weapon.maxAmmo}</div>
                </div>
              )}
            </div>
          </div>
        )}

        <p><strong>Initiative:</strong> {displayUnit.initiative}</p>
        <p><strong>Has Acted:</strong> {displayUnit.hasActedThisTurn ? 'Yes' : 'No'}</p>
        <p><strong>Has Moved:</strong> {displayUnit.hasMovedThisTurn ? 'Yes' : 'No'}</p>

        {statusEffects && statusEffects.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#485e70', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              Active Effects
              <span style={{ flex: 1, height: '1px', background: '#243344', display: 'block' }} />
            </div>
            <div className="status-effects-list">
              {statusEffects.map((effect: StatusEffect) => (
                <div
                  key={effect.id}
                  className={`status-effect status-effect-${effect.effectType.toLowerCase()}`}
                  title={`${effect.effectName}: ${effect.turnsRemaining}/${effect.totalDuration} turns remaining`}
                  style={{ background: '#111e2e', border: '1px solid #243344', borderRadius: '3px', padding: '4px 8px', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <span className="effect-icon">{getEffectIcon(effect.effectType)}</span>
                  <span className="effect-name" style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '12px', color: '#d2c09a', flex: 1 }}>{effect.effectName}</span>
                  <span className="effect-turns" style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '11px', fontWeight: 700, color: '#7a96aa', background: '#162535', padding: '1px 5px', borderRadius: '2px' }}>{effect.turnsRemaining}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BattleUnitInfoPanel;
